import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, History, PackageX, RefreshCw, Send, Trash2, X } from 'lucide-react';
import { damagesAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import { ItemSearchComponent, Modal } from '../ui';
import dayjs from '../../utils/dayjs';
import { printDamageDispatch } from '../../utils/pdf';
import { canSendDamages, DAMAGE_REASONS } from './damageUtils';

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const DamagesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  const isManager = canSendDamages(user);

  const [open, setOpen] = useState({ since: null, items: [], total_quantity: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);

  // Record form
  const [selected, setSelected] = useState(null);
  // The box holds exactly what was typed, so it can be cleared and retyped. Forcing it
  // back to a number on every keystroke turned an empty box into "0", then "03".
  const [quantityText, setQuantityText] = useState('1');
  const [reason, setReason] = useState(DAMAGE_REASONS[0]);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const quantityRef = useRef(null);

  // Send to warehouse
  const [sendOpen, setSendOpen] = useState(false);
  const [sendRemarks, setSendRemarks] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await damagesAPI.listOpen();
      setOpen(res.data);
    } catch (err) {
      addNotification({ type: 'error', title: 'Damages', message: errorText(err, 'Failed to load damages') });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const handleItemSelect = (item) => {
    setSelected(item);
    setQuantityText('1');
    setTimeout(() => quantityRef.current?.select(), 0);
  };

  const resetForm = () => {
    setSelected(null);
    setQuantityText('1');
    setReason(DAMAGE_REASONS[0]);
    setRemarks('');
  };

  const inStock = Math.max(0, Math.floor(Number(selected?.Stock) || 0));
  // The box only ever holds digits (see its onChange), so an empty box is what reads as 0
  // and keeps the button disabled.
  const quantity = /^\d+$/.test(quantityText) ? parseInt(quantityText, 10) : 0;
  const canRecord = selected && quantity >= 1 && quantity <= inStock && reason && !saving;

  const handleRecord = async (e) => {
    e?.preventDefault();
    if (!canRecord) return;
    try {
      setSaving(true);
      await damagesAPI.create({ Item_ID: selected.ID, Quantity: quantity, Reason: reason, Remarks: remarks.trim() || null });
      addNotification({ type: 'success', title: 'Marked damaged', message: `${quantity} x ${selected.Name}` });
      resetForm();
      load();
    } catch (err) {
      addNotification({ type: 'error', title: 'Could not mark damaged', message: errorText(err, 'Failed to record damage') });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (d) => {
    if (!window.confirm(`Remove ${d.Quantity} x ${d.ItemName} from damages? The stock goes back to sellable.`)) return;
    try {
      await damagesAPI.remove(d.ID);
      addNotification({ type: 'success', title: 'Removed', message: `${d.ItemName} is back in stock` });
      load();
    } catch (err) {
      addNotification({ type: 'error', title: 'Could not remove', message: errorText(err, 'Failed to remove damage') });
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      const res = await damagesAPI.dispatch({ Remarks: sendRemarks.trim() || null });
      const dispatch = res.data;
      setSendOpen(false);
      setSendRemarks('');
      addNotification({
        type: 'success',
        title: `${dispatch.DispatchNo} created`,
        message: `${dispatch.TotalQuantity} pieces sent to the warehouse`,
      });
      try {
        await printDamageDispatch(dispatch, formatPrice);
      } catch {
        addNotification({ type: 'error', title: 'PDF', message: 'Saved, but the PDF could not be created. Download it from the shipment page.' });
      }
      navigate(`/damages/dispatches/${dispatch.ID}`);
    } catch (err) {
      addNotification({ type: 'error', title: 'Could not send', message: errorText(err, 'Failed to send damages') });
      load();
    } finally {
      setSending(false);
    }
  };

  const canRemove = (d) => isManager || d.CreatedBy === user?.id;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <PackageX className="h-6 w-6 text-amber-600" />
            Damages
          </h1>
          <p className="text-sm text-gray-500">
            Damaged stock waiting to go back to the warehouse
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/damages/dispatches"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            <History className="h-4 w-4" />
            Sent to warehouse
          </Link>
          {isManager && (
            <button
              type="button"
              onClick={() => setSendOpen(true)}
              disabled={open.items.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
              Send to warehouse
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow px-4 py-3 flex flex-wrap items-center gap-x-8 gap-y-1 text-sm">
        <span className="text-gray-600">
          Since last shipment:{' '}
          <span className="font-medium text-gray-900">
            {open.since ? dayjs(open.since).format('DD MMM YYYY, h:mm A') : 'nothing sent yet'}
          </span>
        </span>
        <span className="text-gray-600">
          Pieces: <span className="font-semibold text-amber-700">{open.total_quantity}</span>
        </span>
        <span className="text-gray-600">
          Value: <span className="font-semibold text-gray-900">{formatPrice(open.total_value)}</span>
        </span>
      </div>

      {/* Record a damage */}
      <form onSubmit={handleRecord} className="bg-white rounded-lg shadow p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Mark an item as damaged</h2>
        {!selected ? (
          <ItemSearchComponent
            placeholder="Scan or search the damaged item..."
            onItemSelect={handleItemSelect}
            showNameSearch={false}
            showOutOfStock={false}
            autoFocus
            compact
          />
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 break-words">{selected.Name}</div>
                <div className="text-xs text-gray-600">
                  Barcode {selected.Barcode || '-'} · {inStock} in stock · {formatPrice(Number(selected.SalePrice) || Number(selected.Price) || 0)}
                </div>
              </div>
              <button type="button" onClick={resetForm} className="p-1 text-gray-500 hover:text-gray-800" title="Choose another item">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[6rem_14rem_minmax(0,1fr)_auto] sm:items-end">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Quantity</span>
                {/* A text box for digits only: no spinner, and a keystroke or paste holding
                    anything else (letters, "-", "." or "e", all of which a number box accepts)
                    is refused, not stripped, so a pasted "1.5" cannot turn into 15. inputMode
                    brings up the number pad on a tablet. */}
                <input
                  ref={quantityRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={quantityText}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) setQuantityText(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Reason</span>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {DAMAGE_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</span>
                <input
                  type="text"
                  value={remarks}
                  maxLength={2000}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g. stain on the collar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </label>
              <button
                type="submit"
                disabled={!canRecord}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                Mark damaged
              </button>
            </div>
            {quantity > inStock && (
              <p className="text-xs text-red-600">Only {inStock} in stock.</p>
            )}
          </div>
        )}
        <p className="text-xs text-gray-500">
          Marked items come out of sellable stock straight away and wait here until they are sent to the warehouse.
        </p>
      </form>

      {/* Open list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Waiting to be sent ({open.items.length})</h2>
          <button type="button" onClick={load} className="p-1 text-gray-500 hover:text-gray-800" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {open.items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            {loading ? 'Loading...' : 'No damaged items since the last shipment.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Item</th>
                  <th className="px-4 py-2 text-right font-medium">Qty</th>
                  <th className="px-4 py-2 text-left font-medium">Reason</th>
                  <th className="px-4 py-2 text-left font-medium">Recorded</th>
                  <th className="px-4 py-2 text-right font-medium">Value</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {open.items.map((d) => (
                  <tr key={d.ID} className="align-top">
                    <td className="px-4 py-2">
                      <Link to={`/items/${d.Item_ID}/history`} className="font-medium text-gray-900 hover:text-blue-700">
                        {d.ItemName || `Item #${d.Item_ID}`}
                      </Link>
                      <div className="text-xs text-gray-500">Barcode {d.Barcode || '-'}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{d.Quantity}</td>
                    <td className="px-4 py-2">
                      <div>{d.Reason}</div>
                      {d.Remarks && <div className="text-xs text-gray-500 break-words">{d.Remarks}</div>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">
                      <div>{dayjs(d.CreatedDate).format('DD MMM YYYY, h:mm A')}</div>
                      <div>{d.CreatedByName || '-'}</div>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{formatPrice(d.LineValue)}</td>
                    <td className="px-4 py-2 text-right">
                      {canRemove(d) && (
                        <button
                          type="button"
                          onClick={() => handleRemove(d)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Remove (puts the stock back)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send confirmation */}
      <Modal isOpen={sendOpen} onClose={() => !sending && setSendOpen(false)} title="Send damages to the warehouse">
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            All <span className="font-semibold">{open.items.length}</span> lines (
            <span className="font-semibold">{open.total_quantity} pieces</span>, {formatPrice(open.total_value)}) go out as one shipment.
            A PDF downloads for the warehouse to check against. Sent items can no longer be removed.
          </p>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Remarks (optional)</span>
            <textarea
              value={sendRemarks}
              onChange={(e) => setSendRemarks(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="e.g. sent with rider, 2 cartons"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSendOpen(false)}
              disabled={sending}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
            >
              {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send and download PDF
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DamagesPage;
